import { useEffect, useRef, useState } from 'react';
import styles from './DropdownToggle.module.css';

export interface DropdownOption<T extends string | number> {
  value: T;
  label: string;
}

interface Props<T extends string | number> {
  options: DropdownOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Optional aria/title prefix, e.g. "Activity mode" → "Activity mode: Total" */
  ariaLabel?: string;
  /** Visual variant: dark = on a dark header, light = on a light surface */
  variant?: 'dark' | 'light';
}

/** Compact toggle — shows only the selected option as a button, opens a
 *  dropdown of all options on click. Replaces inline button-group toggles
 *  to declutter dense headers. */
export function DropdownToggle<T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
  variant = 'dark',
}: Props<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const active = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const root = `${styles.root} ${variant === 'light' ? styles.light : styles.dark}`;

  return (
    <div ref={ref} className={root}>
      <button
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel ? `${ariaLabel}: ${active?.label ?? ''}` : undefined}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{active?.label ?? ''}</span>
        <span className={styles.chevron}>{open ? '\u25B4' : '\u25BE'}</span>
      </button>
      {open && (
        <div className={styles.menu} role="listbox">
          {options.map((opt) => (
            <button
              key={opt.value}
              className={`${styles.item} ${opt.value === value ? styles.itemActive : ''}`}
              role="option"
              aria-selected={opt.value === value}
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
